import React from 'react';
import { FiCheck, FiX, FiTrash2, FiEdit2 } from 'react-icons/fi';
import ManualEvidenceEditor from './ManualEvidenceEditor';
import MoodleBadgeEditor from './MoodleBadgeEditor';
import MoodleActivityEditor from './MoodleActivityEditor';
import SimsysExamEditor from './SimsysExamEditor';

/**
 * Hilfsfunktion um RGB/RGBA-Farben in Hex-Format umzuwandeln
 */
const rgbaToHex = (color) => {
  if (!color || typeof color !== 'string') return '#000000';
  
  // Wenn bereits ein Hex-Wert, gib ihn zurück
  if (color.startsWith('#')) {
    return color;
  }
  
  // RGB/RGBA-Werte extrahieren
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    
    // In Hex umwandeln
    const toHex = (n) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  
  // Fallback
  return '#000000';
};

/**
 * Hilfsfunktion um Datum in YYYY-MM-DD Format umzuwandeln
 */
const formatDateForInput = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Bearbeitungsdialog für MovableBar Komponenten
 */
export default function EditBarDialog({
  editTitle,
  setEditTitle,
  editTextColor,
  setEditTextColor,
  editBackgroundColor,
  setEditBackgroundColor,
  editBorderStyle,
  setEditBorderStyle,
  editBorderColor,
  setEditBorderColor,
  editBorderThickness,
  setEditBorderThickness,
  editStartDate,
  setEditStartDate,
  editEndDate,
  setEditEndDate,
  editLinkEvidence,
  setEditLinkEvidence,
  editEvidenceType,
  setEditEvidenceType,
  editEvidenceLabel,
  setEditEvidenceLabel,
  editEvidenceDueDays,
  setEditEvidenceDueDays,
  editEvidenceData,
  setEditEvidenceData,
  editKlassensynchronisation,
  setEditKlassensynchronisation,
  onSave,
  onCancel,
  onDelete,
  titlePlaceholder = "Titel eingeben"
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [showEvidenceEditor, setShowEvidenceEditor] = React.useState(false);
  const [evidenceData, setEvidenceData] = React.useState(editEvidenceData || { link: '' });

  // Hilfsfunktion für das Sanitizing der Klassennamen
  const sanitizeKlassensynchronisation = (value) => {
    if (!value) return '';
    // Entferne Whitespaces vor und nach Kommas, und entferne leere Einträge
    return value
      .split(',')
      .map(klasse => klasse.trim())
      .filter(klasse => klasse.length > 0)
      .join(',');
  };

  // Update evidenceData when editEvidenceData changes
  React.useEffect(() => {
    if (editEvidenceData) {
      setEvidenceData(editEvidenceData);
    } else {
      // Reset evidenceData for new tasks
      setEvidenceData({ link: '' });
    }
  }, [editEvidenceData]);

  // Auto-fill Bezeichnung with name from evidenceData if empty
  // Only auto-fill if there's already an existing editEvidenceData (not for new tasks)
  React.useEffect(() => {
    if (editEvidenceType && 
        evidenceData && 
        evidenceData.name && 
        editEvidenceData && // Only auto-fill if there's initial evidence data
        (!editEvidenceLabel || editEvidenceLabel.trim() === '')) {
      setEditEvidenceLabel(evidenceData.name);
    }
  }, [evidenceData, editEvidenceType, editEvidenceLabel, setEditEvidenceLabel, editEvidenceData]);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleEditEvidenceClick = () => {
    setShowEvidenceEditor(true);
  };

  const handleEvidenceEditorClose = () => {
    setShowEvidenceEditor(false);
  };

  const handleEvidenceEditorSave = (data) => {
    setEvidenceData(data);
    if (setEditEvidenceData) {
      setEditEvidenceData(data);
    }
  };

  const renderEvidenceEditor = () => {
    switch (editEvidenceType) {
      case 'manual':
        return (
          <ManualEvidenceEditor
            isOpen={showEvidenceEditor}
            onClose={handleEvidenceEditorClose}
            onSave={handleEvidenceEditorSave}
            initialData={evidenceData}
          />
        );
      case 'moodle-badge':
        return (
          <MoodleBadgeEditor
            isOpen={showEvidenceEditor}
            onClose={handleEvidenceEditorClose}
            onSave={handleEvidenceEditorSave}
            initialData={evidenceData}
          />
        );
      case 'moodle-activity':
        return (
          <MoodleActivityEditor
            isOpen={showEvidenceEditor}
            onClose={handleEvidenceEditorClose}
            onSave={handleEvidenceEditorSave}
            initialData={evidenceData}
          />
        );
      case 'simsys-exam':
        return (
          <SimsysExamEditor
            isOpen={showEvidenceEditor}
            onClose={handleEvidenceEditorClose}
            onSave={handleEvidenceEditorSave}
            initialData={evidenceData}
          />
        );
      default:
        return null;
    }
  };
  return (
    <div 
      style={{ 
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100000,
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: '1px solid #ddd',
        minWidth: '400px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto'
      }}
      onClick={e => e.stopPropagation()}


    >
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
        Nachweis bearbeiten
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* Titel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Titel:</label>
          <input
            type="text"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            placeholder={titlePlaceholder}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              flex: 1, 
              minWidth: '200px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            autoFocus
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                if (onSave) {
                  onSave({ evidenceData });
                }
              }
              if (e.key === 'Escape') onCancel();
            }}
            onFocus={e => e.target.style.borderColor = '#4caf50'}
            onBlur={e => e.target.style.borderColor = '#ccc'}
          />
        </div>
        
        {/* Textfarbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Textfarbe:</label>
          <input
            type="color"
            value={rgbaToHex(editTextColor)}
            onChange={e => setEditTextColor(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: '1px solid #ccc', 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={rgbaToHex(editTextColor)}
            onChange={e => setEditTextColor(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#ffffff"
          />
        </div>
        
        {/* Hintergrundfarbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Hintergrund:</label>
          <input
            type="color"
            value={rgbaToHex(editBackgroundColor)}
            onChange={e => setEditBackgroundColor(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: '1px solid #ccc', 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={rgbaToHex(editBackgroundColor)}
            onChange={e => setEditBackgroundColor(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#007bff"
          />
        </div>
        
        {/* Rahmen-Stil */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Stil:</label>
          <select
            value={editBorderStyle}
            onChange={e => setEditBorderStyle(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white',
              minWidth: '150px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <option value="solid">Durchgezogen</option>
            <option value="dashed">Gestrichelt</option>
            <option value="dotted">Gepunktet</option>
            <option value="double">Doppelt</option>
            <option value="groove">Eingeprägt</option>
            <option value="ridge">Erhaben</option>
            <option value="inset">Eingelassen</option>
            <option value="outset">Hervorgehoben</option>
            <option value="none">Kein Rahmen</option>
          </select>
        </div>
        
        {/* Rahmen-Farbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Farbe:</label>
          <input
            type="color"
            value={rgbaToHex(editBorderColor)}
            onChange={e => setEditBorderColor(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: '1px solid #ccc', 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={rgbaToHex(editBorderColor)}
            onChange={e => setEditBorderColor(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#0056b3"
          />
        </div>
        
        {/* Rahmen-Dicke */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Dicke:</label>
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={editBorderThickness}
            onChange={e => setEditBorderThickness(e.target.value)}
            style={{ 
              flex: 1,
              minWidth: '100px',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="number"
            min="0"
            max="20"
            value={editBorderThickness}
            onChange={e => setEditBorderThickness(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '60px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <span style={{ fontSize: '14px', color: '#666' }}>px</span>
        </div>
        
        {/* Startdatum */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Startdatum:</label>
          <input
            type="date"
            value={formatDateForInput(editStartDate)}
            onChange={e => setEditStartDate(e.target.value ? new Date(e.target.value) : null)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        
        {/* Enddatum */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Enddatum:</label>
          <input
            type="date"
            value={formatDateForInput(editEndDate)}
            onChange={e => setEditEndDate(e.target.value ? new Date(e.target.value) : null)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #ccc',
              outline: 'none',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        
        {/* Nachweis verknüpfen */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            <input
              type="checkbox"
              checked={editLinkEvidence}
              onChange={e => setEditLinkEvidence(e.target.checked)}
              style={{ 
                marginRight: '8px',
                cursor: 'pointer'
              }}
              onClick={e => e.stopPropagation()}
            />
            Nachweis verknüpfen
          </label>
        </div>
        
        {/* Bezeichnung (nur wenn Checkbox aktiviert) */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Bezeichnung:</label>
            <input
              type="text"
              value={editEvidenceLabel}
              onChange={e => setEditEvidenceLabel(e.target.value)}
              placeholder="Bezeichnung des Nachweises"
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                flex: 1, 
                minWidth: '200px', 
                borderRadius: '6px', 
                border: '1px solid #ccc',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.target.style.borderColor = '#4caf50'}
              onBlur={e => e.target.style.borderColor = '#ccc'}
            />
          </div>
        )}
        
        {/* Fällig nach x Tagen (nur wenn Checkbox aktiviert) */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Fällig nach:</label>
            <input
              type="number"
              min="1"
              value={editEvidenceDueDays}
              onChange={e => setEditEvidenceDueDays(e.target.value)}
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                width: '80px', 
                borderRadius: '6px', 
                border: '1px solid #ccc',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.target.style.borderColor = '#4caf50'}
              onBlur={e => e.target.style.borderColor = '#ccc'}
            />
            <span style={{ fontSize: '14px', color: '#666' }}>Tagen ab Beginn</span>
          </div>
        )}
        
        {/* Nachweistyp Dropdown (nur wenn Checkbox aktiviert) */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Nachweistyp:</label>
            <select
              value={editEvidenceType}
              onChange={e => setEditEvidenceType(e.target.value)}
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: '1px solid #ccc',
                outline: 'none',
                cursor: 'pointer',
                backgroundColor: 'white',
                minWidth: '200px',
                flex: 1
              }}
              onClick={e => e.stopPropagation()}
            >
              <option value="manual">Manuell</option>
              <option value="moodle-badge">Moodle Badge</option>
              <option value="moodle-activity">Moodle Aktivität</option>
              <option value="simsys-exam">Simsys Prüfung</option>
            </select>
            <button
              style={{
                cursor: 'pointer',
                color: '#666',
                backgroundColor: '#f8f9fa',
                border: '1px solid #ccc',
                padding: '8px 10px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
                minWidth: 'auto'
              }}
              onClick={e => {
                e.stopPropagation();
                handleEditEvidenceClick();
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#e9ecef';
                e.target.style.borderColor = '#adb5bd';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#ccc';
              }}
              title="Nachweistyp bearbeiten"
            >
              <FiEdit2 size={14} />
            </button>
          </div>
        )}

        {/* Klassensynchronisation */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '120px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left', paddingTop: '8px' }}>Klassensynchronisation:</label>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="text"
              value={editKlassensynchronisation || ''}
              onChange={e => setEditKlassensynchronisation && setEditKlassensynchronisation(e.target.value)}
              onBlur={e => {
                e.target.style.borderColor = '#ccc';
                if (setEditKlassensynchronisation) {
                  setEditKlassensynchronisation(sanitizeKlassensynchronisation(e.target.value));
                }
              }}
              placeholder="Klassen kommasepariert (z.B. IT21a, IT21b, IN21a)"
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                width: '100%', 
                borderRadius: '6px', 
                border: '1px solid #ccc',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.target.style.borderColor = '#4caf50'}
            />
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.3'
            }}>
              Geben Sie Klassennamen für die Benutzersynchronisation ein. Mehrere Klassen durch Kommas trennen.
            </div>
          </div>
        </div>
      </div>
      
      {/* Buttons Sektion */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#4caf50',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={e => {
            e.stopPropagation();
            // Gebe evidenceData mit an onSave weiter
            if (onSave) {
              onSave({ evidenceData });
            }
          }}
          onMouseEnter={e => e.target.style.backgroundColor = '#45a049'}
          onMouseLeave={e => e.target.style.backgroundColor = '#4caf50'}
          title="Änderungen speichern"
        >
          <FiCheck />
          Speichern
        </button>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#f44336',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={e => {
            e.stopPropagation();
            onCancel();
          }}
          onMouseEnter={e => e.target.style.backgroundColor = '#da190b'}
          onMouseLeave={e => e.target.style.backgroundColor = '#f44336'}
          title="Änderungen verwerfen"
        >
          <FiX />
          Abbrechen
        </button>
        {onDelete && (
          <button
            style={{ 
              cursor: 'pointer', 
              color: 'white',
              backgroundColor: '#ff5722',
              border: 'none',
              padding: '10px 16px', 
              borderRadius: '6px', 
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onClick={e => {
              e.stopPropagation();
              handleDeleteClick();
            }}
            onMouseEnter={e => e.target.style.backgroundColor = '#d84315'}
            onMouseLeave={e => e.target.style.backgroundColor = '#ff5722'}
            title="Balken löschen"
          >
            <FiTrash2 />
            Löschen
          </button>
        )}
      </div>

      {/* Lösch-Bestätigungsdialog */}
      {showDeleteConfirm && (
        <>
          {/* Backdrop für Bestätigungsdialog */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={handleDeleteCancel}
          >
            {/* Bestätigungsdialog */}
            <div
              style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                border: '1px solid #ddd',
                minWidth: '320px',
                maxWidth: '400px'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#d32f2f' }}>
                Balken löschen?
              </h4>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                Möchten Sie den Balken "{editTitle}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  style={{
                    cursor: 'pointer',
                    color: '#666',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onClick={handleDeleteCancel}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = '#e0e0e0';
                    e.target.style.borderColor = '#ccc';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.borderColor = '#ddd';
                  }}
                >
                  Abbrechen
                </button>
                <button
                  style={{
                    cursor: 'pointer',
                    color: 'white',
                    backgroundColor: '#d32f2f',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onClick={handleDeleteConfirm}
                  onMouseEnter={e => e.target.style.backgroundColor = '#b71c1c'}
                  onMouseLeave={e => e.target.style.backgroundColor = '#d32f2f'}
                  autoFocus
                >
                  <FiTrash2 />
                  Löschen
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Evidence Editor */}
      {renderEvidenceEditor()}
    </div>
  );
}
